/* ==========================================================================
   Cart Rewards Ladder (Feature 1) - free shipping + free gift progress
   --------------------------------------------------------------------------
   Visualisation only. Shopify's own automatic discount rule zeroes the gift
   price at checkout; this file only shows progress and manages the gift line.

   If anything throws, the instance removes itself from the DOM so the cart
   keeps working normally.
   ========================================================================== */
(function () {
  'use strict';

  var C = window.CartRewardsCore;
  if (!C) {
    if (window.console) console.warn('[cart-rewards] core runtime missing - Rewards Ladder disabled');
    return;
  }

  var GIFT_PROPERTIES = { _gift: 'true', _added_by: 'rewards-ladder' };

  function RewardsLadder(host) {
    this.host = host;
    this.cfg = JSON.parse(host.querySelector('[data-arl-config]').textContent);
    this.template = host.querySelector('[data-arl-template]');
    this.milestonesFired = {};
    this.autoAddDone = false;
    this.busy = false;
    this.poolOpen = false;
    this.zonosMajor = null;
    this.preloaded = false;
    this.hadGift = false;
    /* Set if a gift line ever comes back at a non-zero price, i.e. Shopify's
       automatic discount is not doing its job. Latched for the page life. */
    this.giftBlocked = false;
    /* Discounts can settle a beat after the add, so a non-zero price is
       re-checked once before we conclude the discount is off. */
    this.priceCheckDone = false;

    /* If a customer removes the gift we must not silently put it back. The
       decision is remembered for the session and cleared as soon as the cart
       drops below the threshold again. */
    this.declineKey = 'arl:declined:' + (this.cfg.sectionId || 'default');
    this.declined = false;
    try {
      this.declined = sessionStorage.getItem(this.declineKey) === '1';
    } catch (e) {}
  }

  RewardsLadder.prototype.setDeclined = function (value) {
    this.declined = !!value;
    try {
      if (value) sessionStorage.setItem(this.declineKey, '1');
      else sessionStorage.removeItem(this.declineKey);
    } catch (e) {}
    C.log('gift ' + (value ? 'declined by customer' : 'decline cleared'));
  };

  RewardsLadder.prototype.init = function () {
    var self = this;

    this.mounts = C.createMounts({
      key: 'arl',
      order: 10,
      template: this.template,
      config: this.cfg,
      onMount: function (root, location) {
        self.bind(root, location);
        if (self.lastState) self.paint(root, self.lastState);
      }
    });

    if (this.cfg.shipping.source === 'zonos') {
      C.thresholds.watchZonos(this.cfg.market, function (major) {
        self.zonosMajor = major;
        self.recompute();
      });
    }

    this.unsubscribe = C.bus.subscribe(function (cart) { self.render(cart); });

    if (this.cfg.designMode) {
      /* Paint something immediately, then follow the merchant's real cart. The
         core runtime skips its own initial fetch in design mode, so ask for it. */
      this.render(this.demoCart());
      document.addEventListener('shopify:section:load', function () { self.mounts.sync(); });
      C.bus.refresh();
    }

    C.log('Rewards Ladder ready', this.cfg);
  };

  RewardsLadder.prototype.destroy = function () {
    try { if (this.unsubscribe) this.unsubscribe(); } catch (e) {}
    this.unsubscribe = null;
    try { if (this.mounts) this.mounts.destroy(); } catch (e) {}
  };

  RewardsLadder.prototype.demoCart = function () {
    var count = this.cfg.demo.itemCount || 0;
    var subtotal = C.money.toActiveMinor(this.cfg.demo.subtotal || 0) || 0;
    return {
      item_count: count,
      total_price: subtotal,
      items_subtotal_price: subtotal,
      items: count
        ? [{ key: 'demo:1', variant_id: 0, quantity: count, final_line_price: subtotal, original_line_price: subtotal, properties: null }]
        : []
    };
  };

  RewardsLadder.prototype.recompute = function () {
    var cart = C.bus.cart || (this.cfg.designMode ? this.demoCart() : null);
    if (cart) this.render(cart);
  };

  /* In the theme editor an empty cart falls back to the preview numbers so the
     merchant sees a realistic card; a cart with items always wins. */
  RewardsLadder.prototype.effectiveCart = function (cart) {
    if (cart && cart.item_count > 0) return cart;
    if (this.cfg.designMode) return this.demoCart();
    return cart;
  };

  /* ---------------------------------------------------------------- state */

  RewardsLadder.prototype.resolveShipping = function () {
    var cfg = this.cfg.shipping;
    var market = this.cfg.market;

    if (cfg.source === 'off') return { minor: null, via: 'disabled' };

    var overrides = C.thresholds.parseMarketMap(cfg.byMarketRaw);
    if (market && overrides[market] != null) {
      return { minor: C.money.majorToMinor(overrides[market]), via: 'merchant per-market override' };
    }

    if (cfg.source === 'zonos') {
      if (this.zonosMajor === null) this.zonosMajor = C.thresholds.zonos(market);
      if (this.zonosMajor !== null) return { minor: C.money.majorToMinor(this.zonosMajor), via: 'Zonos runtime' };
    }

    if (cfg.source === 'zonos' || cfg.source === 'shopify') {
      var mf = C.thresholds.parseMetafield(cfg.metafieldRaw, market);
      if (mf !== null) return { minor: C.money.toActiveMinor(mf), via: 'shop.metafields.zonos.free_shipping_thresholds' };
    }

    var manual = C.num(cfg.manual);
    if (manual !== null && manual > 0) {
      return { minor: C.money.toActiveMinor(manual), via: 'manual override setting' };
    }

    return { minor: null, via: 'unresolved - milestone hidden' };
  };

  RewardsLadder.prototype.resolveGift = function () {
    var g = this.cfg.gift;
    /* We tried and the gift did not come back free, so there is no honest gift
       offer to show. Hide the whole gift half rather than leave a dead button. */
    if (this.giftBlocked) {
      return { minor: null, via: 'gift discount not active - milestone hidden' };
    }
    var overrides = C.thresholds.parseMarketMap(g.byMarketRaw);
    if (this.cfg.market && overrides[this.cfg.market] != null) {
      return { minor: C.money.majorToMinor(overrides[this.cfg.market]), via: 'per-market override' };
    }
    var base = C.num(g.threshold);
    if (base === null || base <= 0) return { minor: null, via: 'not set' };
    if (!g.featured && !(g.poolIds || []).length) return { minor: null, via: 'gift collection empty - milestone hidden' };
    if (!this.claimableVariantId()) {
      return { minor: null, via: 'no gift in stock - milestone hidden' };
    }
    return { minor: C.money.toActiveMinor(base), via: 'gift_threshold setting' };
  };

  /* The variant a claim would actually add: the featured gift if it is in
     stock, otherwise the first in-stock product in the gift collection. */
  RewardsLadder.prototype.claimableVariantId = function () {
    var g = this.cfg.gift;
    var pool = g.poolIds || [];
    var available = g.poolAvailable || [];
    /* The Buy X Get Y discount only zeroes products inside the gift collection,
       so a featured product that is not in that collection is not claimable -
       adding it would charge the customer full price. */
    if (g.featured && g.featured.available && g.featured.variantId) {
      var inPool = pool.length === 0 || pool.indexOf(g.featured.variantId) !== -1;
      if (inPool) return g.featured.variantId;
      C.warn(
        'featured gift "' + g.featured.title + '" is not in the gift collection - ' +
          'the discount would not make it free, so it is being skipped'
      );
    }
    return available.length ? available[0] : null;
  };

  /* Image, title and product URL for a gift variant, read off the rendered pool
     inside a MOUNTED instance. It has to be the mount, not the host: the host
     only holds a <template>, whose content is not queryable with
     querySelector. Used so the featured card mirrors the selected gift. */
  function giftMediaFrom(scope, variantId) {
    if (!scope || !variantId) return null;
    var item = scope.querySelector('[data-arl-pool-item][data-variant-id="' + variantId + '"]');
    if (!item) return null;
    return {
      url: item.getAttribute('data-url'),
      image: item.getAttribute('data-image'),
      title: item.getAttribute('data-product-title')
    };
  }

  RewardsLadder.prototype.subtotalOf = function (cart) {
    var basis = this.cfg.subtotalBasis === 'before' ? 'original_line_price' : 'final_line_price';
    var poolIds = this.cfg.gift.poolIds || [];
    var total = 0;
    (cart.items || []).forEach(function (item) {
      if (C.isGiftLine(item, poolIds)) return;
      var v = item[basis];
      if (v == null) v = item.line_price;
      if (v == null) v = item.final_line_price;
      total += C.num(v) || 0;
    });
    return total;
  };

  RewardsLadder.prototype.giftLine = function (cart) {
    var poolIds = this.cfg.gift.poolIds || [];
    var found = null;
    (cart.items || []).forEach(function (item) {
      if (!found && C.isGiftLine(item, poolIds)) found = item;
    });
    return found;
  };

  /* --------------------------------------------------------------- render */

  RewardsLadder.prototype.render = function (cart) {
    if (!cart) return; // cart fetch failed - leave the last painted state
    var self = this;
    cart = this.effectiveCart(cart);

    var ship = this.resolveShipping();
    var gift = this.resolveGift();
    var subtotal = this.subtotalOf(cart);
    var currency = C.money.info().active;
    var locale = this.cfg.language;

    if (ship.minor === null && gift.minor === null) {
      C.log('no thresholds resolved (shipping: ' + ship.via + ', gift: ' + gift.via + ') - hiding');
      this.mounts.roots().forEach(function (r) { r.setAttribute('data-arl-state', 'hidden'); });
      return;
    }

    C.log('thresholds - shipping:', ship.minor, '(' + ship.via + ')', 'gift:', gift.minor, '(' + gift.via + ')');

    var max = Math.max(ship.minor || 0, gift.minor || 0);
    var state = 'below-shipping';
    if (gift.minor !== null && subtotal >= gift.minor) state = 'at-gift';
    else if (ship.minor !== null && subtotal >= ship.minor) state = 'between';
    else if (ship.minor === null) state = 'between';

    var needShip = ship.minor !== null ? Math.max(0, ship.minor - subtotal) : 0;
    var needGift = gift.minor !== null ? Math.max(0, gift.minor - subtotal) : 0;

    var tags = {
      amount_needed: '<b>' + C.esc(C.money.format(state === 'below-shipping' ? needShip : needGift, currency, locale)) + '</b>',
      shipping_threshold: ship.minor !== null ? C.money.format(ship.minor, currency, locale) : '',
      gift_threshold: gift.minor !== null ? C.money.format(gift.minor, currency, locale) : ''
    };

    var message;
    if (state === 'at-gift') {
      message = this.cfg.copy.atGift;
    } else if (state === 'between') {
      /* no gift milestone to point at, so use the first sentence of the
         "between" copy - "Free shipping unlocked." - rather than a bare label */
      message = gift.minor !== null
        ? this.cfg.copy.between
        : String(this.cfg.copy.between || '').split('.')[0] || this.cfg.labels.shippingUnlocked;
    } else {
      message = this.cfg.copy.belowShipping;
    }
    message = C.mergeTags(message, tags);

    var giftLine = this.giftLine(cart);

    /* The card always shows the gift that is actually on offer: the one in the
       cart if there is one, otherwise whatever a claim would add. */
    var claimable = this.claimableVariantId();
    var mediaVariant = giftLine ? giftLine.variant_id : claimable;

    var view = {
      state: state,
      percent: max ? C.clamp((subtotal / max) * 100, 0, 100) : 0,
      messageHTML: message,
      messageText: message.replace(/<[^>]+>/g, ''),
      subtotal: subtotal,
      currency: currency,
      locale: locale,
      markers: [
        ship.minor !== null && {
          kind: 'shipping',
          percent: max ? C.clamp((ship.minor / max) * 100, 0, 100) : 0,
          reached: subtotal >= ship.minor,
          label: subtotal >= ship.minor ? this.cfg.labels.shippingUnlocked : this.cfg.labels.shipping,
          tip: C.mergeTags('{label} at {amount}', {
            label: this.cfg.labels.shipping,
            amount: C.money.format(ship.minor, currency, locale)
          })
        },
        gift.minor !== null && {
          kind: 'gift',
          percent: max ? C.clamp((gift.minor / max) * 100, 0, 100) : 0,
          reached: subtotal >= gift.minor,
          label: subtotal >= gift.minor ? this.cfg.labels.giftUnlocked : this.cfg.labels.gift,
          tip: C.mergeTags('{label} at {amount}', {
            label: this.cfg.labels.gift,
            amount: C.money.format(gift.minor, currency, locale)
          })
        }
      ].filter(Boolean),
      giftUnlocked: gift.minor !== null && subtotal >= gift.minor,
      giftThresholdLabel: gift.minor !== null ? C.money.format(gift.minor, currency, locale) : '',
      selectedVariantId: mediaVariant,
      giftInCart: !!giftLine,
      /* Only a line that actually costs nothing may be labelled FREE GIFT. */
      giftLineFree: !!giftLine && (C.num(giftLine.final_line_price) || 0) === 0,
      hasGift: gift.minor !== null,
      claimableVariantId: claimable,
      giftLineTitle: giftLine ? giftLine.product_title || giftLine.title : null,
      giftTitle: giftLine
        ? giftLine.product_title || giftLine.title
        : this.cfg.gift.featured
        ? this.cfg.gift.featured.title
        : ''
    };

    this.lastState = view;
    this.mounts.sync();
    this.mounts.roots().forEach(function (root) { self.paint(root, view); });
    this.decorateGiftLines(view);

    if (!this.cfg.designMode) {
      this.fireMilestones(view, gift, ship, subtotal, currency);
      this.maybePreload(subtotal, gift.minor);
      this.reconcileGift(cart, view);
    }
  };

  RewardsLadder.prototype.paint = function (root, view) {
    root.setAttribute('data-arl-state', view.state);

    var fill = root.querySelector('[data-arl-fill]');
    if (fill) fill.style.width = view.percent.toFixed(2) + '%';

    var bar = root.querySelector('[data-arl-bar]');
    if (bar) {
      bar.setAttribute('aria-valuenow', String(Math.round(view.percent)));
      bar.setAttribute('aria-valuetext', view.messageText);
    }

    var msg = root.querySelector('[data-arl-message]');
    if (msg) msg.innerHTML = view.messageHTML;

    var byKind = {};
    view.markers.forEach(function (m) { byKind[m.kind] = m; });

    [].forEach.call(root.querySelectorAll('[data-arl-marker]'), function (el) {
      var kind = el.getAttribute('data-arl-marker');
      var m = byKind[kind];
      if (!m) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      el.style.left = m.percent.toFixed(2) + '%';
      el.setAttribute('data-arl-edge', m.percent >= 98 ? 'end' : m.percent <= 2 ? 'start' : 'mid');

      var isNext = !m.reached && view.markers.filter(function (x) { return !x.reached && x.percent < m.percent; }).length === 0;
      el.setAttribute('data-arl-status', m.reached ? 'reached' : isNext ? 'current' : 'upcoming');

      var tip = el.querySelector('[data-arl-marker-tip]');
      if (tip) tip.textContent = m.tip;
      var sr = el.querySelector('[data-arl-marker-sr]');
      if (sr) sr.textContent = m.tip + (m.reached ? ' - unlocked' : ' - not yet reached');
    });

    /* Legend row: a flex row under the bar, so two milestones can never
       overlap however narrow the drawer gets. */
    [].forEach.call(root.querySelectorAll('[data-arl-legend-item]'), function (el) {
      var m = byKind[el.getAttribute('data-arl-legend-item')];
      if (!m) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      var isNext = !m.reached && view.markers.filter(function (x) { return !x.reached && x.percent < m.percent; }).length === 0;
      el.setAttribute('data-arl-status', m.reached ? 'reached' : isNext ? 'current' : 'upcoming');
      var text = el.querySelector('[data-arl-legend-text]');
      if (text) text.textContent = m.label;
      el.title = m.tip;
    });

    var gift = root.querySelector('[data-arl-gift]');
    if (gift) {
      /* No gift milestone on the bar means no gift card either. */
      gift.hidden = !view.hasGift;
      gift.setAttribute('data-arl-locked', view.giftUnlocked ? 'false' : 'true');

      /* Mirror the selected gift: link, image and name all follow the choice. */
      var media = giftMediaFrom(gift, view.selectedVariantId);

      var link = gift.querySelector('[data-arl-gift-link]');
      if (link && media && media.url) link.setAttribute('href', media.url);

      var img = gift.querySelector('img.arl-gift__img');
      if (img && media && media.image && img.getAttribute('src') !== media.image) {
        img.removeAttribute('srcset');
        img.setAttribute('src', media.image);
      }

      var eyebrow = gift.querySelector('[data-arl-gift-eyebrow]');
      if (eyebrow) {
        eyebrow.textContent = view.giftUnlocked
          ? this.cfg.copy.giftEyebrow
          : C.mergeTags(this.cfg.copy.giftLocked, { gift_threshold: view.giftThresholdLabel });
      }

      var claim = gift.querySelector('[data-arl-claim]');
      if (claim) {
        claim.disabled = !view.giftUnlocked || this.busy || !view.claimableVariantId || this.giftBlocked;
        claim.setAttribute('data-arl-added', view.giftInCart ? 'true' : 'false');
        claim.textContent = view.giftInCart ? this.cfg.copy.claimed : this.cfg.copy.claim;
      }

      [].forEach.call(gift.querySelectorAll('[data-arl-pool-item]'), function (item) {
        var selected = String(item.getAttribute('data-variant-id')) === String(view.selectedVariantId) && view.giftInCart;
        item.setAttribute('aria-checked', selected ? 'true' : 'false');
        item.tabIndex = selected ? 0 : -1;
      });
      if (!gift.querySelector('[data-arl-pool-item][aria-checked="true"]')) {
        var first = gift.querySelector('[data-arl-pool-item]');
        if (first) first.tabIndex = 0;
      }

      var title = gift.querySelector('[data-arl-gift-title]');
      var resolvedTitle = view.giftLineTitle || (media && media.title) || view.giftTitle;
      if (title && resolvedTitle) title.textContent = resolvedTitle;
    }

    C.liveAnnounce(root.querySelector('[data-arl-live]'), view.messageText);
  };

  /* ------------------------------------------------------------ gift line */

  /* Locks the gift line in the cart UI (no qty stepper, no remove button)
     without editing the theme's cart-item snippet. */
  RewardsLadder.prototype.decorateGiftLines = function (view) {
    /* Never label a line FREE GIFT unless it is genuinely free. A charged line
       carrying that badge is the worst thing this feature could do, so any
       stale decoration is stripped before we consider adding it. */
    if (!view.giftLineFree || !view.selectedVariantId) {
      [].forEach.call(document.querySelectorAll('[data-arl-gift-line]'), function (row) {
        row.removeAttribute('data-arl-gift-line');
        var badge = row.querySelector('.arl-gift-line-badge');
        if (badge) badge.remove();
      });
      return;
    }
    var id = String(view.selectedVariantId);
    [].forEach.call(document.querySelectorAll('.cart-item, .cart-drawer__cart-items > *, tr.cart-item'), function (row) {
      var link = row.querySelector('a[href*="variant="], a[href*="/variants/"]');
      var match = link && link.getAttribute('href').indexOf(id) !== -1;
      if (!match) {
        var input = row.querySelector('[data-variant-id="' + id + '"]');
        match = !!input;
      }
      if (!match) return;
      row.setAttribute('data-arl-gift-line', 'true');
      if (!row.querySelector('.arl-gift-line-badge')) {
        var anchor = row.querySelector('.cart-item__details, .cart-item__name, td, .cart-item__info');
        if (anchor) {
          var badge = document.createElement('span');
          badge.className = 'arl-gift-line-badge';
          badge.textContent = 'FREE GIFT';
          anchor.insertBefore(badge, anchor.firstChild);
        }
      }
    });
  };

  RewardsLadder.prototype.reconcileGift = function (cart, view) {
    var self = this;
    if (this.busy) return;

    var line = this.giftLine(cart);

    /* The gift is only free because Shopify's own automatic discount zeroes it.
       If that discount is not active the line comes back at full price, so pull
       it straight back out - never let a line the cart labels FREE GIFT be
       charged for - and stop offering it for the rest of the page life.

       `view.giftUnlocked` is essential, not defensive. When the cart drops back
       under the threshold the discount CORRECTLY stops zeroing the gift, so the
       line reverts to full price for the moment before we remove it. Without
       this check that expected price change reads as a dead discount, latches
       giftBlocked, and hides the gift half for the rest of the page - so the
       customer who removes one item never sees the gift offered again, even
       after they go back over the threshold. Below the threshold a charged gift
       line is normal and the removal branch further down deals with it. */
    if (line && view.giftUnlocked && (C.num(line.final_line_price) || 0) > 0) {
      /* Look again before judging - Shopify sometimes reports the line before
         the automatic discount has been allocated to it. */
      if (!this.priceCheckDone) {
        this.priceCheckDone = true;
        C.log('gift line is not free yet - re-checking in a moment');
        C.bus.schedule(1500);
        return;
      }
      this.busy = true;
      this.giftBlocked = true;
      this.autoAddDone = true;
      this.hadGift = false;
      C.warn(
        'gift line came back at ' + line.final_line_price + ' minor units instead of 0 - ' +
          'the Buy X Get Y automatic discount is not active, so the gift has been ' +
          'removed rather than charged for'
      );
      C.bus
        .changeById(line.key, 0)
        .catch(function (e) { C.warn('gift rollback failed', e); })
        .then(function () {
          self.busy = false;
          C.bus.schedule(0);
        });
      return;
    }

    /* A free gift line, or no gift line at all, means nothing is pending. */
    this.priceCheckDone = false;

    /* Gift was there a moment ago, the cart still qualifies, and we didn't
       remove it - so the customer did. Respect that: no auto re-add. The Claim
       button and the gift picker both stay live so they can change their mind. */
    if (!line && this.hadGift && view.giftUnlocked) {
      this.setDeclined(true);
    }
    this.hadGift = !!line;

    /* Dropping below the threshold is a clean slate: the decline is forgotten
       and auto-add is armed again. Resetting here rather than only when we
       remove a gift line also covers the cart being cleared or emptied. */
    if (!view.giftUnlocked) {
      if (this.declined) this.setDeclined(false);
      this.autoAddDone = false;
    }

    if (line && line.quantity > 1) {
      this.busy = true;
      C.warn('gift line quantity was ' + line.quantity + ' - clamping to 1');
      C.bus
        .changeById(line.key, 1)
        .catch(function (e) { C.warn('gift clamp failed', e); })
        .then(function () {
          self.busy = false;
          C.bus.schedule(0);
        });
      return;
    }

    if (!view.giftUnlocked && line) {
      this.busy = true;
      C.log('below gift threshold - removing gift line', line.key);
      C.bus
        .changeById(line.key, 0)
        .then(function () { self.autoAddDone = false; })
        .catch(function (e) { C.warn('gift removal failed', e); })
        .then(function () {
          self.busy = false;
          C.bus.schedule(0);
        });
      return;
    }

    if (view.giftUnlocked && !line && this.cfg.gift.autoAdd && !this.autoAddDone && !this.declined && !this.giftBlocked) {
      var variantId = view.claimableVariantId;
      if (!variantId) {
        C.warn('auto-add skipped - no gift in the collection is in stock');
        return;
      }
      var featured = this.cfg.gift.featured || {};
      this.autoAddDone = true;
      this.addGift(variantId, featured.title, 'auto');
    }
  };

  RewardsLadder.prototype.addGift = function (variantId, title, source) {
    var self = this;
    if (this.busy) return Promise.resolve();
    if (this.giftBlocked) {
      C.warn('gift not added - the automatic discount that makes it free is not active');
      return Promise.resolve();
    }
    this.busy = true;
    if (source !== 'auto') this.setDeclined(false);

    var cart = C.bus.cart || { items: [] };
    var existing = this.giftLine(cart);
    var chain = Promise.resolve();

    if (existing && String(existing.variant_id) !== String(variantId)) {
      chain = C.bus.changeById(existing.key, 0);
    } else if (existing) {
      this.busy = false;
      return Promise.resolve();
    }

    return chain
      .then(function () {
        return C.bus.add([{ id: variantId, quantity: 1, properties: GIFT_PROPERTIES }]);
      })
      .then(function () {
        if (self.cfg.analytics) {
          C.track(source === 'auto' ? 'gift_claimed' : 'gift_selected', {
            gift_variant_id: variantId,
            gift_title: title,
            source: source,
            cart_value: (C.bus.cart ? C.bus.cart.total_price : 0) / 100,
            currency: C.money.info().active
          });
        }
      })
      .catch(function (e) {
        C.warn('gift add failed', e);
      })
      .then(function () {
        self.busy = false;
        C.bus.schedule(0);
      });
  };

  /* -------------------------------------------------------------- binding */

  RewardsLadder.prototype.bind = function (root) {
    var self = this;

    var claim = root.querySelector('[data-arl-claim]');
    if (claim) {
      claim.addEventListener('click', function () {
        if (claim.disabled) return;
        var view = self.lastState;
        var variantId = (view && view.claimableVariantId) || claim.getAttribute('data-variant-id');
        if (!variantId) return;
        self.addGift(variantId, claim.getAttribute('data-product-title'), 'claim');
      });
    }

    var toggle = root.querySelector('[data-arl-toggle-pool]');
    var pool = root.querySelector('[data-arl-pool]');
    if (toggle && pool) {
      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        pool.hidden = !open;
        var label = toggle.querySelector('span');
        if (label) label.textContent = open ? self.cfg.copy.seeLess : self.cfg.copy.seeAll;
        if (open && self.cfg.analytics && !self.cfg.designMode) {
          C.track('gift_pool_opened', {
            cart_value: (C.bus.cart ? C.bus.cart.total_price : 0) / 100,
            currency: C.money.info().active
          });
        }
      });
    }

    if (pool) {
      pool.addEventListener('click', function (e) {
        var t = e.target;
        var item = t && t.closest ? t.closest('[data-arl-pool-item]') : null;
        if (!item || item.disabled) return;
        if (!self.lastState || !self.lastState.giftUnlocked) return;
        self.addGift(item.getAttribute('data-variant-id'), item.getAttribute('data-product-title'), 'pool');
      });

      /* radiogroup keyboard support */
      pool.addEventListener('keydown', function (e) {
        var items = [].filter.call(pool.querySelectorAll('[data-arl-pool-item]'), function (i) { return !i.disabled; });
        if (!items.length) return;
        var i = items.indexOf(document.activeElement);
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = items[(i + 1) % items.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = items[(i - 1 + items.length) % items.length];
        else if (e.key === 'Home') next = items[0];
        else if (e.key === 'End') next = items[items.length - 1];
        if (next) {
          e.preventDefault();
          next.focus();
        }
      });
    }
  };

  /* ------------------------------------------------------------ analytics */

  RewardsLadder.prototype.fireMilestones = function (view, gift, ship, subtotal, currency) {
    if (!this.cfg.analytics) return;
    var base = { cart_value: subtotal / 100, currency: currency };

    if (!this.milestonesFired.viewed) {
      this.milestonesFired.viewed = true;
      C.track('rewards_ladder_viewed', base);
    }

    var self = this;
    view.markers.forEach(function (m) {
      var key = 'ms_' + m.kind;
      if (m.reached && !self.milestonesFired[key]) {
        self.milestonesFired[key] = true;
        C.track('rewards_ladder_milestone_reached', Object.assign({ milestone_name: m.kind === 'shipping' ? 'free_shipping' : 'free_gift' }, base));
      } else if (!m.reached && self.milestonesFired[key]) {
        self.milestonesFired[key] = false;
      }
    });
  };

  RewardsLadder.prototype.maybePreload = function (subtotal, giftMinor) {
    if (this.preloaded || !giftMinor) return;
    var featured = this.cfg.gift.featured;
    if (!featured || !featured.image) return;
    if (subtotal < giftMinor * 0.8) return;
    this.preloaded = true;
    var link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = featured.image;
    document.head.appendChild(link);
    C.log('preloaded featured gift image');
  };

  /* ----------------------------------------------------------- bootstrap */

  function boot() {
    var hosts = [].slice.call(document.querySelectorAll('[data-arl-host]'));
    if (!hosts.length) return;
    /* If the merchant placed the section twice (overlay group + cart template)
       only the first instance drives the UI. */
    if (window.__arlInstance && window.__arlInstance.host.isConnected) return;

    for (var i = 0; i < hosts.length; i++) {
      try {
        var instance = new RewardsLadder(hosts[i]);
        instance.init();
        window.__arlInstance = instance;
        return;
      } catch (e) {
        C.warn('Rewards Ladder failed to start - removing itself', e);
        try {
          var stale = document.querySelectorAll('.arl-mount');
          for (var j = 0; j < stale.length; j++) stale[j].remove();
        } catch (e2) {}
      }
    }
  }

  C.onReady(boot);
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-arl-host]')) {
      if (window.__arlInstance) {
        try { window.__arlInstance.destroy(); } catch (err) {}
        window.__arlInstance = null;
      }
      boot();
    }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-arl-host]') && window.__arlInstance) {
      try { window.__arlInstance.destroy(); } catch (err) {}
      window.__arlInstance = null;
    }
  });
})();